import React, { useEffect, useState } from 'react'
import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getLayoutByIdApi,
  LayoutResponseType,
  LayoutRowType,
} from '@app-store'
import { GroupBehaviorProvider } from '../GroupBehaviorProvider'
import { FieldValues, useFormContext } from 'react-hook-form'
import { layoutCache } from './layoutcache'
import { isEmpty, merge } from 'lodash'
import { HybridLayoutParsedRowLayoutMapper } from './HybridLayoutParsedRowLayoutMapper'
import {
  buildQuery,
  mergeNestedObjects,
  normalizeLookups,
} from '../../utils/hybridFormRendererfuntions.utils'
import { SkeletonLoader } from '@ui-kit'
import {
  HybridLayoutDefaultRowPropsType,
  HybridLayoutDefaultRowWrapperPropsType,
} from './hybridLayout.types'
import {
  ENTITY_FIELDS,
  ENTITY_NAME,
  HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS,
} from '../../utils/constants'
import { STRINGS } from '../../utils/strings'
import {
  hasHybridLayoutFormRules,
  HybridFormBehaviorScope,
  triggerHybridFormBehaviorHydrate,
} from '../../form-behavior/HybridFormBehaviorScope'

export const HybridLayoutDefaultRow = React.memo(
  ({
    layoutData,
    recordId,
    isAssociated = false,
    associatedObject,
    formUniqueId,
    refetchTimer,
    refetchLogic,
    openAllAccordions,
    customInRowComponent,
    rootRowIndex,
    mode,
    task,
  }: HybridLayoutDefaultRowPropsType) => {
    const rows: LayoutRowType[] =
      layoutData?.layout_data?.pages?.[0]?.rows || []
    return (
      <div>
        {rows.map((nestedRow, idx) => {
          const key = nestedRow.rowId || `row-${idx}`
          return (
            <React.Fragment key={key}>
              <HybridLayoutParsedRowLayoutMapper
                row={nestedRow}
                recordId={recordId}
                fullLayout={layoutData}
                isAssociated={isAssociated}
                associatedObject={associatedObject}
                formUniqueId={formUniqueId}
                refetchTimer={refetchTimer}
                refetchLogic={refetchLogic}
                openAllAccordions={openAllAccordions}
                customInRowComponent={customInRowComponent}
                rootRowIndex={rootRowIndex}
                rowIndex={idx}
                mode={mode}
                task={task}
              />
            </React.Fragment>
          )
        })}
      </div>
    )
  }
)
HybridLayoutDefaultRow.displayName = 'HybridLayoutDefaultRow'

export const HybridLayoutDefaultRowWrapper: React.FC<HybridLayoutDefaultRowWrapperPropsType> =
  React.memo(
    ({
      row,
      entityId,
      recordId,
      formUniqueId,
      refetchTimer,
      refetchLogic,
      openAllAccordions,
      rootRowIndex,
      mode,
      task,
    }) => {
      const { reset, getValues, setValue } = useFormContext()
      const layoutId = row?.meta?.layoutId
      const cacheKey = layoutId && entityId ? `${entityId}:${layoutId}` : ''

      const [layoutObject, setLayoutObject] =
        useState<LayoutResponseType | null>(null)

      const [isLoading, setIsLoading] = useState(!layoutObject && !!cacheKey)
      const [error, setError] = useState(false)

      const fetchData = async (
        payload: FieldValues,
        nestedLayout?: LayoutResponseType | null
      ) => {
        if (!recordId) return
        const requestPayload = structuredClone(payload)
        if (row?.meta?.customInRowComponent) {
          row?.meta?.customInRowComponent?.forEach((comp) => {
            if (
              comp.key ===
              HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS.APPLICATION_VERSION
            ) {
              if (
                !requestPayload.select?.includes(ENTITY_NAME.CASE_PAP_DETAILS)
              ) {
                requestPayload.select.push(ENTITY_NAME.CASE_PAP_DETAILS)
              }
              requestPayload.related_entities[ENTITY_NAME.CASE_PAP_DETAILS] = {
                ...requestPayload.related_entities?.[
                  ENTITY_NAME.CASE_PAP_DETAILS
                ],
                select: [
                  ...(requestPayload.related_entities?.[
                    ENTITY_NAME.CASE_PAP_DETAILS
                  ]?.select || []),
                  ENTITY_FIELDS.CASE_PAP_DETAILS.acceptable_application_version,
                ],
              }
            }
            if (
              comp.key ===
              HYBRID_LAYOUT_COUSTOM_IN_ROW_COMPONENTS_CONSTANTS.APPLICATION_FORM_VERSION
            ) {
              if (
                !requestPayload.select?.includes(ENTITY_NAME.CASE_PAP_DETAILS)
              ) {
                requestPayload.select.push(ENTITY_NAME.CASE_PAP_DETAILS)
              }
              requestPayload.related_entities[ENTITY_NAME.CASE_PAP_DETAILS] = {
                ...requestPayload.related_entities?.[
                  ENTITY_NAME.CASE_PAP_DETAILS
                ],
                select: [
                  ...(requestPayload.related_entities?.[
                    ENTITY_NAME.CASE_PAP_DETAILS
                  ]?.select || []),
                  ENTITY_FIELDS.CASE_PAP_DETAILS
                    .c_case_pap_application_form_versions,
                ],
              }
            }
          })
        }
        try {
          const response = await getGenericEntityListingAPI(
            requestPayload as GetGenericEntityListingApiType,
            recordId
          )
          if (response) {
            const getOriginalValues = getValues(
              '__FORM_ORIGINAL_VALUES_EXTRA_FIELD__'
            )
            const normalized = normalizeLookups(response)
            const val = getValues()
            delete val.__FORM_ORIGINAL_VALUES_EXTRA_FIELD__
            const endData = mergeNestedObjects(val, normalized, '')
            reset(endData)
            if (isEmpty(getOriginalValues)) {
              setValue('__FORM_ORIGINAL_VALUES_EXTRA_FIELD__', response)
            } else {
              const mergedOriginal = merge(getOriginalValues, response)
              setValue('__FORM_ORIGINAL_VALUES_EXTRA_FIELD__', mergedOriginal)
            }
            // Nested BASE_LAYOUT (e.g. Insurance Information) owns its own form_rules;
            // hydrate after reset so rules see loaded values (incl. Uninsured).
            if (hasHybridLayoutFormRules(nestedLayout)) {
              triggerHybridFormBehaviorHydrate(nestedLayout?.uid)
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error)
        }
      }

      useEffect(() => {
        let active = true
        if (!layoutId || !entityId) {
          setLayoutObject(null)
          setIsLoading(false)
          return
        }

        const key = `${entityId}:${layoutId}`

        const fetchLayout = async () => {
          setIsLoading(true)
          setError(false)
          try {
            const response = await getLayoutByIdApi({
              entityId: String(entityId),
              layoutId: String(layoutId),
            })
            if (!active) return
            if (response?.data) {
              // form-behavior keys off layout.uid; nested layout APIs sometimes omit it
              const nestedLayout: LayoutResponseType = {
                ...response.data,
                uid:
                  response.data.uid || `layout-${response.data.id ?? layoutId}`,
              }
              layoutCache.set(key, nestedLayout)
              setLayoutObject(nestedLayout)
            } else {
              setLayoutObject(null)
            }
          } catch (e) {
            if (active) {
              // eslint-disable-next-line no-console
              console.error('Failed to fetch layout', e)
              setError(true)
              setLayoutObject(null)
            }
          } finally {
            if (active) setIsLoading(false)
          }
        }

        fetchLayout()

        return () => {
          active = false
        }
      }, [layoutId, entityId, refetchTimer])

      // Fetch entity data only after nested layout mounts with HybridFormBehaviorScope,
      // so hydrate can find a registered evaluator (same pattern as HybridLayoutSubForm).
      useEffect(() => {
        if (!layoutObject || !entityId || !recordId) return

        const data = buildQuery(entityId, [
          {
            isAssociated: false,
            addedFields: layoutObject?.layout_data?.added_fields ?? {},
          },
        ])

        void fetchData(data, layoutObject)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [layoutObject, entityId, recordId, refetchTimer])

      if (!layoutId || !entityId) return <div>{STRINGS.NO_LAYOUT_SELECTED}</div>
      if (isLoading) return <SkeletonLoader rows={4} columns={4} />
      if (error || isEmpty(layoutObject))
        return <div>{STRINGS.NO_LAYOUT_FOUND}</div>

      return (
        <HybridFormBehaviorScope
          layout={layoutObject}
          formUniqueId={formUniqueId}
          setValue={setValue}
          getValues={getValues}
          reset={reset}
          mode={mode}
        >
          <GroupBehaviorProvider
            groups={layoutObject.layout_data?.groups}
            pages={layoutObject.layout_data?.pages ?? []}
            entityName={'macros'}
            recordId={recordId}
          >
            <HybridLayoutDefaultRow
              layoutData={layoutObject}
              recordId={recordId}
              formUniqueId={formUniqueId}
              refetchTimer={refetchTimer}
              refetchLogic={refetchLogic}
              openAllAccordions={openAllAccordions}
              customInRowComponent={row?.meta?.customInRowComponent}
              rootRowIndex={rootRowIndex}
              mode={mode}
              task={task}
            />
          </GroupBehaviorProvider>
        </HybridFormBehaviorScope>
      )
    }
  )
HybridLayoutDefaultRowWrapper.displayName = 'HybridLayoutDefaultRowWrapper'
